import { useState, useMemo } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import {
  api, type EngagementContract, type ContractChangeOrder, type ContractFigure,
  type ContractType, type ContractInput, type ChangeOrderInput,
} from '../lib/api';
import { fmtCurrency } from '../lib/fmt';
import {
  Card, CardHeader, Table, Th, Tr, Td, Badge, ActionButton, Spinner, EmptyState,
} from './ui';
import { BudgetTimelineChart } from './BudgetTimelineChart';

// ─── Engagement contract terms + change orders ──────────────────────────────
//
// The maintenance surface for what the client signed. Two audiences reach it:
// onboarding (capture, usually deal-keyed before the Jira engagement exists)
// and the budget report (maintenance, engagement-keyed). Same component, so
// the figures can't drift between where they're entered and where they're read.
//
// Reads need report_engagement_budget; writes need admin_engagement_contracts.
// Without the write key the panel still renders in full — finance staff who
// only review terms shouldn't get an empty box.

const TYPES: Array<{ id: ContractType; label: string; help: string }> = [
  { id: 'fixed_fee',    label: 'Fixed fee',      help: 'Sold for one price.' },
  { id: 'estimate',     label: 'Estimate',       help: 'Low / median / high band. May be exceeded — approval is obtained in HubSpot and Jira, not here.' },
  { id: 'estimate_sne', label: 'Estimate + SNE', help: 'The band plus a shall-not-exceed ceiling. Passing SNE is a breach of terms.' },
];

/** Figures each type carries AS A BASELINE, in the order they're displayed and
 *  entered. This is the capture form's shape — the DB constrains it. */
const TYPE_FIGURES: Record<ContractType, ContractFigure[]> = {
  fixed_fee:    ['fixed_fee'],
  estimate:     ['estimate_low', 'estimate_median', 'estimate_high'],
  estimate_sne: ['estimate_low', 'estimate_median', 'estimate_high', 'not_to_exceed'],
};

/** Figures a CHANGE ORDER may move — wider than the baseline set, because an
 *  estimate contract can be given a shall-not-exceed ceiling it wasn't signed
 *  with once burn starts worrying the client. Mirrors CHANGE_ORDER_FIGURES in
 *  engagementContractService. */
const CO_FIGURES: Record<ContractType, ContractFigure[]> = {
  fixed_fee:    ['fixed_fee'],
  estimate:     ['estimate_low', 'estimate_median', 'estimate_high', 'not_to_exceed'],
  estimate_sne: ['estimate_low', 'estimate_median', 'estimate_high', 'not_to_exceed'],
};

/** The figures this contract actually HAS: its baseline set, plus anything a
 *  change order established. The server omits figures nothing has touched, so
 *  presence in `contract.figures` is the authority — that's what keeps a plain
 *  estimate from displaying an empty "Shall-not-exceed" it never agreed to. */
function figuresOf(contract: EngagementContract): ContractFigure[] {
  return CO_FIGURES[contract.contract_type].filter((f) => contract.figures[f] != null);
}

const FIGURE_LABEL: Record<ContractFigure, string> = {
  fixed_fee: 'Fixed fee',
  estimate_low: 'Low',
  estimate_median: 'Median',
  estimate_high: 'High',
  not_to_exceed: 'Shall-not-exceed',
};

export function EngagementContractPanel({
  engagementId,
  dealId,
  /** Shown above the panel so the operator knows what they're editing. */
  subject,
  /** Hides the over-time chart — it needs an engagement to have worklogs. */
  showTimeline = true,
}: {
  engagementId?: string | null;
  dealId?: string | null;
  subject?: string;
  showTimeline?: boolean;
}) {
  const { user } = useAuth();
  const canEdit = user?.permissions?.includes('admin_engagement_contracts') === true;

  const { data, isLoading, error, refetch } = useQuery(
    () => api.engagementContracts.find({
      engagementId: engagementId ?? undefined,
      dealId: dealId ?? undefined,
    }),
    [engagementId, dealId],
  );

  // Local copy so a save/add returns the recomputed contract straight into the
  // view without a refetch round-trip showing stale totals in between.
  const [local, setLocal] = useState<EngagementContract | null>(null);
  // Bumped on every mutation, and the timeline's refetch trigger. It has to be
  // a counter rather than anything derived from the contract: an edited change
  // order and a corrected baseline both move the chart's threshold zones while
  // leaving the change-order COUNT untouched, so keying off the count left the
  // chart showing pre-edit figures until something added or removed a row.
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState(false);
  const contract = local ?? data?.contract ?? null;

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;

  /** Every write lands here — the recomputed contract into the view, and the
   *  version forward so anything fetched separately re-reads. */
  const applyContract = (c: EngagementContract) => {
    setLocal(c);
    setVersion((v) => v + 1);
  };

  const onSaved = (c: EngagementContract) => { applyContract(c); setEditing(false); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {subject && (
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{subject}</div>
      )}

      {!contract && !editing && (
        <Card>
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>No contract terms captured.</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              Until these are entered, the budget report falls back to the Jira
              budget field and has no band to check against.
            </div>
            {canEdit
              ? <ActionButton onClick={() => setEditing(true)} variant="primary">Capture terms</ActionButton>
              : <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  You don’t have permission to capture them.
                </div>}
          </div>
        </Card>
      )}

      {editing && (
        <ContractForm
          existing={contract}
          engagementId={engagementId ?? null}
          dealId={dealId ?? null}
          onCancel={() => setEditing(false)}
          onSaved={onSaved}
        />
      )}

      {contract && !editing && (
        <>
          <TermsCard contract={contract} canEdit={canEdit} onEdit={() => setEditing(true)} />
          <ChangeOrdersCard contract={contract} canEdit={canEdit} onChanged={applyContract} />
          {showTimeline && engagementId && (
            <TimelineCard engagementId={engagementId} refreshKey={version} />
          )}
        </>
      )}

      {/* Refetch escape hatch — the local copy can go stale if someone else
          edits the same contract in another tab. */}
      {contract && !editing && (
        <div style={{ textAlign: 'right' }}>
          <ActionButton onClick={() => { setLocal(null); refetch(); }} variant="default">
            Reload from server
          </ActionButton>
        </div>
      )}
    </div>
  );
}

// ─── Terms ──────────────────────────────────────────────────────────────────

function TermsCard({ contract, canEdit, onEdit }: {
  contract: EngagementContract; canEdit: boolean; onEdit: () => void;
}) {
  const figures = figuresOf(contract);
  const typeLabel = TYPES.find((t) => t.id === contract.contract_type)?.label ?? contract.contract_type;
  const hasChangeOrders = contract.change_orders.length > 0;
  const hasPending = contract.change_orders.some((co) => !co.is_effective);

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Contract terms</span>
        <Badge variant="blue">{typeLabel}</Badge>
        {contract.contract_reference && (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            {contract.contract_reference}
          </span>
        )}
        {contract.signed_date && (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
            signed {contract.signed_date}
          </span>
        )}
        {contract.source === 'hubspot' && <Badge variant="gray">from HubSpot</Badge>}
        <span style={{ marginLeft: 'auto' }}>
          {canEdit && <ActionButton onClick={onEdit} variant="default">Edit terms</ActionButton>}
        </span>
      </CardHeader>

      <Table>
        <thead>
          <tr>
            <Th>Figure</Th>
            <Th right>As signed</Th>
            {hasChangeOrders && <Th right>Change orders</Th>}
            <Th right>Current</Th>
            {hasPending && <Th right>Once all COs land</Th>}
          </tr>
        </thead>
        <tbody>
          {figures.map((f) => {
            const state = contract.figures[f];
            if (!state) return null;
            const moved = state.applied_delta !== 0;
            return (
              <Tr key={f}>
                <Td bold>{FIGURE_LABEL[f]}</Td>
                <Td right muted>{state.baseline != null ? fmtCurrency(state.baseline) : '—'}</Td>
                {hasChangeOrders && (
                  <Td right muted style={{ color: moved ? 'var(--color-text)' : undefined }}>
                    {moved ? signed(state.applied_delta) : '—'}
                  </Td>
                )}
                <Td right bold>{state.current != null ? fmtCurrency(state.current) : '—'}</Td>
                {hasPending && (
                  <Td right muted>
                    {state.pending_delta !== 0 && state.future != null
                      ? fmtCurrency(state.future)
                      : '—'}
                  </Td>
                )}
              </Tr>
            );
          })}
        </tbody>
      </Table>

      {hasPending && (
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-text-muted)' }}>
          “Current” excludes change orders whose effective date hasn’t arrived —
          it’s the ceiling in force today.
        </div>
      )}
      {contract.notes && (
        <div style={{ padding: '8px 16px', fontSize: 11, borderTop: '0.5px solid var(--color-border)' }}>
          {contract.notes}
        </div>
      )}
    </Card>
  );
}

const signed = (n: number): string => `${n > 0 ? '+' : '−'}${fmtCurrency(Math.abs(n))}`;

// ─── Change orders ──────────────────────────────────────────────────────────

function ChangeOrdersCard({ contract, canEdit, onChanged }: {
  contract: EngagementContract;
  canEdit: boolean;
  onChanged: (c: EngagementContract) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const figures = figuresOf(contract);

  const remove = async (co: ContractChangeOrder) => {
    if (!confirm(
      `Remove ${co.reference ? `change order ${co.reference}` : 'this change order'}?\n\n`
      + 'This is for correcting mis-entry. A change order that was signed and '
      + 'later reversed should be entered as its own reversing change order so '
      + 'the history survives.',
    )) return;
    setBusy(true); setErr(null);
    try {
      const res = await api.engagementContracts.deleteChangeOrder(co.id);
      onChanged(res.contract);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Change orders</span>
        <Badge variant={contract.change_orders.length === 0 ? 'gray' : 'blue'}>
          {contract.change_orders.length}
        </Badge>
        <span style={{ marginLeft: 'auto' }}>
          {canEdit && !adding && (
            <ActionButton onClick={() => { setAdding(true); setEditingId(null); }} variant="primary">
              Add change order
            </ActionButton>
          )}
        </span>
      </CardHeader>

      {err && (
        <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--color-red-text)' }}>{err}</div>
      )}

      {adding && (
        <ChangeOrderForm
          contract={contract}
          onCancel={() => setAdding(false)}
          onSaved={(c) => { onChanged(c); setAdding(false); }}
        />
      )}

      {contract.change_orders.length === 0 && !adding ? (
        <EmptyState message="No change orders. The terms above are as originally signed." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Reference</Th>
              <Th>Effective</Th>
              <Th>Scope change</Th>
              {figures.map((f) => <Th key={f} right>{FIGURE_LABEL[f]}</Th>)}
              <Th>Status</Th>
              {canEdit && <Th>{''}</Th>}
            </tr>
          </thead>
          <tbody>
            {contract.change_orders.map((co) => (
              editingId === co.id ? (
                <tr key={co.id}>
                  <td colSpan={figures.length + (canEdit ? 5 : 4)} style={{ padding: 0 }}>
                    <ChangeOrderForm
                      contract={contract}
                      existing={co}
                      onCancel={() => setEditingId(null)}
                      onSaved={(c) => { onChanged(c); setEditingId(null); }}
                    />
                  </td>
                </tr>
              ) : (
                <Tr key={co.id}>
                  <Td bold>{co.reference ?? '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>{co.effective_date}</Td>
                  <Td style={{ fontSize: 11, maxWidth: 320 }}>{co.scope_change}</Td>
                  {figures.map((f) => {
                    const d = co.deltas[f];
                    return (
                      <Td key={f} right muted={d == null}>
                        {d == null ? '—' : signed(d)}
                      </Td>
                    );
                  })}
                  <Td>
                    {co.is_effective
                      ? <Badge variant="green">In force</Badge>
                      : <Badge variant="amber">From {co.effective_date}</Badge>}
                  </Td>
                  {canEdit && (
                    <Td>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <ActionButton onClick={() => { setEditingId(co.id); setAdding(false); }} variant="default">
                          Edit
                        </ActionButton>
                        <ActionButton onClick={() => remove(co)} variant="danger" disabled={busy}>
                          Remove
                        </ActionButton>
                      </span>
                    </Td>
                  )}
                </Tr>
              )
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ─── Forms ──────────────────────────────────────────────────────────────────

const inputStyle = {
  fontSize: 12, padding: '4px 8px', width: '100%',
  border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
  color: 'var(--color-text)', fontFamily: 'inherit',
} as const;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>{hint}</span>}
    </label>
  );
}

/** Parse a money input. Empty → null so "untouched" stays distinguishable
 *  from zero — a change order that sets a figure to +0 is not the same as one
 *  that leaves it alone. */
function money(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t.replace(/[, ]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function ContractForm({ existing, engagementId, dealId, onCancel, onSaved }: {
  existing: EngagementContract | null;
  engagementId: string | null;
  dealId: string | null;
  onCancel: () => void;
  onSaved: (c: EngagementContract) => void;
}) {
  const [type, setType] = useState<ContractType>(existing?.contract_type ?? 'estimate_sne');
  const initial = (f: ContractFigure) => {
    const b = existing?.figures[f]?.baseline;
    return b == null ? '' : String(b);
  };
  const [vals, setVals] = useState<Record<ContractFigure, string>>({
    fixed_fee: initial('fixed_fee'),
    estimate_low: initial('estimate_low'),
    estimate_median: initial('estimate_median'),
    estimate_high: initial('estimate_high'),
    not_to_exceed: initial('not_to_exceed'),
  });
  const [reference, setReference] = useState(existing?.contract_reference ?? '');
  const [signedDate, setSignedDate] = useState(existing?.signed_date ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const figures = TYPE_FIGURES[type];
  const set = (f: ContractFigure, v: string) => setVals((s) => ({ ...s, [f]: v }));

  const submit = async () => {
    setBusy(true); setErr(null);
    const input: ContractInput = {
      jira_engagement_issue_id: engagementId,
      hubspot_deal_id: dealId,
      contract_type: type,
      fixed_fee_amount: money(vals.fixed_fee),
      estimate_low: money(vals.estimate_low),
      estimate_median: money(vals.estimate_median),
      estimate_high: money(vals.estimate_high),
      not_to_exceed_amount: money(vals.not_to_exceed),
      contract_reference: reference.trim() || null,
      signed_date: signedDate.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      const res = await api.engagementContracts.save(input);
      onSaved(res.contract);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>
          {existing ? 'Edit contract terms' : 'Capture contract terms'}
        </span>
      </CardHeader>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {existing && (
          <div style={{
            fontSize: 11, color: 'var(--color-amber-text)', background: 'var(--color-amber-bg)',
            padding: '6px 10px', borderRadius: 'var(--radius-sm)',
          }}>
            This edits the figures <strong>as originally signed</strong> — use it to
            correct a mis-keyed number. To record a negotiated change, add a change
            order instead so the history is preserved.
          </div>
        )}

        <Field label="How was this sold?">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => setType(t.id)}
                title={t.help}
                style={{
                  fontSize: 11, padding: '5px 12px', borderRadius: 'var(--radius-sm)',
                  border: type === t.id ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
                  background: type === t.id ? 'var(--color-blue-bg)' : 'var(--color-surface)',
                  color: type === t.id ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
                  fontFamily: 'inherit',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 10, color: 'var(--color-text-hint)' }}>
            {TYPES.find((t) => t.id === type)?.help}
          </span>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${figures.length}, minmax(0,1fr))`, gap: 10 }}>
          {figures.map((f) => (
            <Field
              key={f}
              label={FIGURE_LABEL[f]}
              hint={f === 'not_to_exceed' ? 'As negotiated — not derived from High' : undefined}
            >
              <input
                type="text"
                inputMode="decimal"
                value={vals[f]}
                onChange={(e) => set(f, e.target.value)}
                placeholder="0.00"
                style={inputStyle}
              />
            </Field>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10 }}>
          <Field label="Contract reference" hint="SOW / MSA number as filed">
            <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Signed date">
            <input type="date" value={signedDate} onChange={(e) => setSignedDate(e.target.value)} style={inputStyle} />
          </Field>
        </div>

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        </Field>

        {err && <div style={{ fontSize: 11, color: 'var(--color-red-text)' }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <ActionButton onClick={onCancel} variant="default">Cancel</ActionButton>
          <ActionButton onClick={submit} variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save terms'}
          </ActionButton>
        </div>
      </div>
    </Card>
  );
}

function ChangeOrderForm({ contract, existing, onCancel, onSaved }: {
  contract: EngagementContract;
  existing?: ContractChangeOrder;
  onCancel: () => void;
  onSaved: (c: EngagementContract) => void;
}) {
  // The wider set: this form is where an estimate contract acquires an SNE.
  const figures = CO_FIGURES[contract.contract_type];
  const [reference, setReference] = useState(existing?.reference ?? '');
  const [scope, setScope] = useState(existing?.scope_change ?? '');
  const [effective, setEffective] = useState(existing?.effective_date ?? new Date().toISOString().slice(0, 10));
  const [deltas, setDeltas] = useState<Record<ContractFigure, string>>({
    fixed_fee: existing?.deltas.fixed_fee != null ? String(existing.deltas.fixed_fee) : '',
    estimate_low: existing?.deltas.estimate_low != null ? String(existing.deltas.estimate_low) : '',
    estimate_median: existing?.deltas.estimate_median != null ? String(existing.deltas.estimate_median) : '',
    estimate_high: existing?.deltas.estimate_high != null ? String(existing.deltas.estimate_high) : '',
    not_to_exceed: existing?.deltas.not_to_exceed != null ? String(existing.deltas.not_to_exceed) : '',
  });
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (f: ContractFigure, v: string) => setDeltas((s) => ({ ...s, [f]: v }));

  // Live preview of where each figure lands. The whole reason deltas are the
  // input is that nobody should have to work out the cumulative effect by
  // hand — so the running total is shown as they type.
  const preview = useMemo(() => {
    const out: Partial<Record<ContractFigure, { from: number | null; to: number | null }>> = {};
    for (const f of figures) {
      // No state at all is the establishing case — an SNE this contract doesn't
      // have yet. It previews as "— → $X", the same shape as every other
      // figure, because that is exactly what a delta against a null baseline
      // does on the server.
      const state = contract.figures[f];
      const d = money(deltas[f]);
      // When editing, back out this change order's own contribution so the
      // preview doesn't double-count it — but only if it's actually in
      // `current`, which it isn't while the change order is future-dated.
      const own = existing?.is_effective ? (existing.deltas[f] ?? 0) : 0;
      const base = state?.current == null ? null : state.current - own;
      out[f] = { from: base, to: base == null ? (d ?? null) : base + (d ?? 0) };
    }
    return out;
  }, [contract, deltas, figures, existing]);

  const submit = async () => {
    setBusy(true); setErr(null);
    const input: ChangeOrderInput = {
      reference: reference.trim() || null,
      scope_change: scope,
      effective_date: effective,
      delta_fixed_fee: money(deltas.fixed_fee),
      delta_estimate_low: money(deltas.estimate_low),
      delta_estimate_median: money(deltas.estimate_median),
      delta_estimate_high: money(deltas.estimate_high),
      delta_not_to_exceed: money(deltas.not_to_exceed),
      notes: notes.trim() || null,
    };
    try {
      const res = existing
        ? await api.engagementContracts.updateChangeOrder(existing.id, input)
        : await api.engagementContracts.addChangeOrder(contract.id, input);
      onSaved(res.contract);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      padding: 16, background: 'var(--color-bg)',
      borderBottom: '0.5px solid var(--color-border)',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 10 }}>
        <Field label="Reference" hint="CO number as filed">
          <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Effective date" hint="When it takes effect, not when it was signed">
          <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Scope change" hint="What actually changed">
          <input type="text" value={scope} onChange={(e) => setScope(e.target.value)} style={inputStyle} />
        </Field>
      </div>

      <div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 6 }}>
          How does it move the budget? Leave a field blank if this change order
          doesn’t touch it. Negative values are a descope.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${figures.length}, minmax(0,1fr))`, gap: 10 }}>
          {figures.map((f) => {
            const p = preview[f];
            const d = money(deltas[f]);
            return (
              <Field key={f} label={FIGURE_LABEL[f]}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={deltas[f]}
                  onChange={(e) => set(f, e.target.value)}
                  placeholder="—"
                  style={inputStyle}
                />
                <span style={{ fontSize: 10, color: d == null ? 'var(--color-text-hint)' : 'var(--color-text-muted)' }}>
                  {d == null || !p
                    ? 'unchanged'
                    : `${p.from != null ? fmtCurrency(p.from) : '—'} → ${p.to != null ? fmtCurrency(p.to) : '—'}`}
                </span>
              </Field>
            );
          })}
        </div>
      </div>

      <Field label="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
      </Field>

      {err && <div style={{ fontSize: 11, color: 'var(--color-red-text)' }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <ActionButton onClick={onCancel} variant="default">Cancel</ActionButton>
        <ActionButton onClick={submit} variant="primary" disabled={busy}>
          {busy ? 'Saving…' : existing ? 'Save change order' : 'Add change order'}
        </ActionButton>
      </div>
    </div>
  );
}

// ─── Timeline ───────────────────────────────────────────────────────────────

function TimelineCard({ engagementId, refreshKey }: { engagementId: string; refreshKey: number }) {
  const { data, isLoading, error } = useQuery(
    () => api.admin.engagementBudgetTimeline(engagementId),
    [engagementId, refreshKey],
  );
  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Budget and expenditure over time</span>
      </CardHeader>
      <div style={{ padding: 16 }}>
        {isLoading ? <Spinner />
          : error ? <EmptyState message={error} />
          : data ? <BudgetTimelineChart timeline={data} />
          : null}
      </div>
    </Card>
  );
}
